import { t } from "i18next";
import React, { useState } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import { NOTIFICATIONS } from "@const/notifications";

import useCustomerQuery from "@hooks/useCustomerQuery";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IFormData {
  password: string;
  newPassword: string;
  repeatNewPassword: string;
}

function ChangePasswordForm() {
  const { customer } = useCustomerStore();
  const { openToast } = useLayoutStore();

  const { changePassword, createToken } = useCustomerQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [firstErrorKey, setFirstErrorKey] = useState<string>("");

  /**
   * 입력 form hook
   * email, password Input 입력값 handler
   * */
  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormData>({});

  const onSubmit: SubmitHandler<IFormData> = async (formData, event) => {
    if (!event) return;
    event.preventDefault();

    if (isLoading) return;

    try {
      if (!customer.email) {
        return;
      }

      setIsLoading(true);

      let accessToken: string | undefined;
      await createToken({
        variables: { input: { email: customer.email, password: String(getValues("password")) } },
        onCompleted: ({ customerAccessTokenCreate }) => {
          const error = customerAccessTokenCreate.customerUserErrors[0];
          if (error?.code === "UNIDENTIFIED_CUSTOMER") {
            openToast(NOTIFICATIONS.WARNING_PASSWORD_NOT_MATCH);
          }

          if (customerAccessTokenCreate.customerAccessToken) {
            accessToken = String(customerAccessTokenCreate.customerAccessToken.accessToken);

            if (getValues("password") === getValues("newPassword")) {
              openToast(NOTIFICATIONS.WARNING_PASSWORD_SAME_AS_BEFORE);
              return new Error(error.message);
            }
          }
        },
        onError: (error) => {
          return new Error(error.message);
        },
      });

      if (!accessToken) return;
      await changePassword({
        variables: {
          customerAccessToken: accessToken,
          customer: { password: String(getValues("newPassword")) },
        },
      });

      reset();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      throw new Error(message);
    } finally {
      // reset();
      setIsLoading(false);
    }
  };

  const displayError = (key: string) => {
    const isError = Boolean(errors[firstErrorKey as keyof typeof errors]) && firstErrorKey === key;
    return {
      isError,
      errorMessage: isError ? errors[key as keyof typeof errors]?.message : "",
    };
  };

  const onError: SubmitErrorHandler<IFormData> = (error) => {
    const sortedKeys = Object.keys(getValues());
    sortedKeys.some((key) => {
      /**
       * typeof sortedKeys === (keyof IFormData)[]이지만 Object.keys로 가져왔기 때문에 string[].
       * error는 FieldError<IFormData> 타입으로 error의 key는 IFormData의 key와 같음.
       * sortedKeys의 key로 error[key]를 찾아서 에러가 있는지 확인하는데, 이때 key는 `keyof typeof error`로 타입 단언 했음.
       */
      if (error[key as keyof typeof error]) {
        setFirstErrorKey(key);
        return true;
      }
      return false;
    });
  };

  return (
    <form className="flex flex-col gap-2.5" onSubmit={handleSubmit(onSubmit, onError)}>
      <Input
        id="password"
        label={t("current_password")}
        placeholder={t("current_password")}
        type="password"
        {...register("password", {
          validate(value: string) {
            if (!value?.trim()) return t("error.necessary_password");
          },
        })}
        {...displayError("password")}
      />
      <Input
        id="newPassword"
        label={t("new_password")}
        placeholder={t("new_password")}
        type="password"
        {...register("newPassword", {
          validate(value: string) {
            const _value = (value ?? "")?.trim();
            if (!_value) return t("error.necessary_new_password");
            if (_value.length < 5) return t("login.min_password_title");
          },
        })}
        {...displayError("newPassword")}
      />
      <Input
        id="repeatNewPassword"
        label={t("confirm_new_password")}
        placeholder={t("confirm_new_password")}
        type="password"
        {...register("repeatNewPassword", {
          validate(value: string) {
            const _value = (value ?? "")?.trim();
            if (!_value) return t("error.necessary_confirm_password");
            if (_value !== getValues("newPassword")) return t("error.match_password");
          },
        })}
        {...displayError("repeatNewPassword")}
      />
      <Button as="square" disabled={isLoading} loading={isLoading} type="submit">
        {t("account.reset_password.request")}
      </Button>
    </form>
  );
}

export default ChangePasswordForm;
